<template>
  <div ref="categoryTiles" class="category-tiles">
    <Carousel
      :coverflow="{ mobile: true, desktop: false }"
      :visible-items="{ mobile: 1, desktop: 3, fullhd: 4 }"
      :gap="20"
      :arrows="{ mobile: false, desktop: true }"
      align-items="flex-start"
    >
      <CarouselItem v-for="(category, index) in categories" :key="index">
        <category-tile ref="headerContainer" :header-link="getLink(category)">
          <template v-if="getLink(category)" #header>
            <!-- Home -->
            <template v-if="checkType(category, 'plisy')">
              <pleated-blinds-icon />
            </template>
            <template v-if="checkType(category, 'roletki')">
              <roller-blinds-icon />
            </template>
            <template v-if="checkType(category, 'zaslony')">
              <curtains-icon />
            </template>
            <template v-if="checkType(category, 'relax')">
              <relax-icon />
            </template>

            <!-- Plisy -->
            <template v-if="checkType(category, 'bezinwazyjne')">
              <pleated-blinds-without-drilling-icon />
            </template>
            <template v-if="checkType(category, 'inwazyjne')">
              <pleated-blinds-with-drilling-icon />
            </template>
            <template v-if="checkType(category, 'dachowe')">
              <pleated-skylight-roof-blinds-icon />
            </template>

            <!-- Rolety -->
            <template v-if="checkType(category, 'DayAndNight')">
              <roller-blinds-day-night-icon />
            </template>
            <template v-if="checkType(category, 'Thermo')">
              <roller-blinds-thermo-blackout-icon />
            </template>

            <!-- Zaslony -->
            <template v-if="checkType(category, 'Century')">
              <curtains-icon />
            </template>
            <template v-if="checkType(category, 'Millenium')">
              <curtains-icon />
            </template>

            <!-- Relax -->
            <template v-if="checkType(category, 'Spannbettlaken')">
              <relax-icon />
            </template>
            <template v-if="checkType(category, 'Kissenbezüge')">
              <relax-icon />
            </template>

            <div class="category-header-container">
              <h3 v-if="category.description" class="category-description">
                {{ category.description.name }}
              </h3>
              <rating-average :average="category.rate.rate ? category.rate.rate : 5" :total="category.rate.count" />
            </div>
          </template>
          <template #links>
            <nuxt-link
              v-for="(link, i) in category.landingPages"
              :key="i"
              :to="localePath({
                name: 'product',
                params: {
                  productName: link.link,
                  productId: link.offerProduct}
              })"
              class="category-link"
            >
              <div class="category-link-value">
                <template v-if="link.name">
                  <span>
                    {{ link.name }}
                  </span>
                </template>
                <!-- TODO: Do wyjebania jak bedzie pelna zwrotka z backendu - brakuje category.landingPages[0].name -->
                <template v-else>
                  <span>
                    {{ category.description.name }}
                  </span>
                </template>
                <span v-if="link.new" class="category-link-new">
                  {{ $t('project.landingPage.new') }}
                </span>
              </div>
              <img src="~/assets/images/arrow-cta.svg" alt="arrow" class="category-link-arrow">
            </nuxt-link>
          </template>
          <template #image>
            <nuxt-link v-if="getLink(category) && category.imageGroup" :to="getLink(category)">
              <b-image
                ratio="1by1"
                :src="getImageBySize(category.imageGroup, 'big').src"
                :webp-fallback="getImageBySize(category.imageGroup, 'big').webpFallback"
                class="skeleton-image-placeholder"
                :alt="category.offerProductName || 'schuette product'"
                lazy
              >
                <template #placeholder>
                  <b-skeleton />
                </template>
              </b-image>
            </nuxt-link>
            <template v-else>
              <b-image
                ratio="1by1"
                :src="getImageBySize(category.imageGroup, 'big').src"
                :webp-fallback="getImageBySize(category.imageGroup, 'big').webpFallback"
                class="skeleton-image-placeholder"
                :alt="category.offerProductName || 'schuette product'"
                lazy
              >
                <template #placeholder>
                  <b-skeleton />
                </template>
              </b-image>
            </template>
          </template>
          <template v-if="full" #colors>
            <div v-if="getColorsByCategory(category)" class="category-collection-colors">
              <div class="collection-colors-container">
                <div class="collection-colors">
                  <div
                    v-for="(color, index2) in getColorsByCategory(category).colors"
                    :key="index2"
                    class="collection-color"
                    :style="`background-color: ${color}`"
                  />
                </div>
                <div v-if="getColorsByCategory(category).plusMore" class="collection-plus-more">
                  +{{ $tc('products.countColors', getColorsByCategory(category).plusMore) }}
                </div>
              </div>
            </div>
          </template>

          <template v-if="full" #features>
            <slot
              name="features"
              :identifier="category.identifier"
              :identifierSeo="category.description ? category.description.name_seo : null"
            />
          </template>
          <template #buttons>
            <!-- <nuxt-link v-if="getLink(category)" :to="getLink(category)"> -->
            <nuxt-link v-if="getLinkProduct(category)" :to="getLinkProduct(category)">
              <b-button type="is-primary" class="cta-primary" expanded rounded icon-right="arrow-right-bold">
                {{ $t('project.landingPage.browseAll') }}
              </b-button>
            </nuxt-link>
          </template>
        </category-tile>
      </CarouselItem>
    </Carousel>
  </div>
</template>

<script>
import Carousel from '~/marketplace-front-core/components/shared/Carousel.vue'
import CarouselItem from '~/marketplace-front-core/components/shared/CarouselItem.vue'
import CategoryTile from '~/components/shared/CategoryTile'
import RatingAverage from '~/components/layout/RatingAverage'

import PleatedBlindsIcon from '~/assets/images/category/pb.svg?inline'
import RollerBlindsIcon from '~/assets/images/category/rb.svg?inline'
import CurtainsIcon from '~/assets/images/category/cr.svg?inline'
import RelaxIcon from '~/assets/images/category/ac.svg?inline'

import PleatedBlindsWithoutDrillingIcon from '~/assets/images/category/pbwd.svg?inline'
import PleatedBlindsWithDrillingIcon from '~/assets/images/category/pbd.svg?inline'
import PleatedSkylightRoofBlindsIcon from '~/assets/images/category/psrb.svg?inline'

import RollerBlindsDayNightIcon from '~/assets/images/category/rbdn.svg?inline'
import RollerBlindsThermoBlackoutIcon from '~/assets/images/category/rbtb.svg?inline'

import equalHeight from '~/mixin/equalHeight'

export default {
  components: {
    Carousel,
    CarouselItem,
    CategoryTile,
    RatingAverage,
    PleatedBlindsIcon,
    RollerBlindsIcon,
    CurtainsIcon,
    RelaxIcon,
    PleatedBlindsWithoutDrillingIcon,
    PleatedBlindsWithDrillingIcon,
    PleatedSkylightRoofBlindsIcon,
    RollerBlindsDayNightIcon,
    RollerBlindsThermoBlackoutIcon
  },
  mixins: [equalHeight],
  props: {
    categories: {
      type: Array,
      default () {
        return []
      }
    },
    full: {
      type: Boolean
    },
    home: {
      type: Boolean
    }
  },
  data () {
    return {
      colors: {
        plisy: {
          colors: ['#f9f9f9', '#e8e5ce', '#d5cdaa', '#d6c491', '#b3a491', '#a7a7a7', '#525252', '#d61d1f', '#df6a3d', '#dfcf68', '#78cf40', '#87cfc5', '#576195', '#21317d', '#505b73', '#a06559', '#e69f52', '#98899a', '#727272', '#424242', '#242424'],
          plusMore: 57
        },
        roletki: {
          colors: ['#f6f6f6', '#e9e3dd', '#ede6c2', '#d8cb64', '#f0c787', '#c06a2b', '#dd6932', '#dfbbbb', '#c0312a', '#822529', '#b6b6b6', '#665c5b', '#57483b', '#493a33', '#7f814a', '#46b19a', '#7a7f96', '#545454', '#2f475f', '#2c3249', '#262626'],
          plusMore: 5
        },
        zaslony: {
          colors: ['#ededed', '#ddddda', '#9b9989', '#95866d', '#886d2b', '#c09c5f', '#bb4e35', '#4b5a25', '#3e524b', '#293b2f', '#3e2431', '#692426', '#822b3c', '#952020', '#a79fa1', '#80817a', '#5c5f5e', '#4d4744', '#3c3c3c', '#17232f', '#171717'],
          plusMore: 11
        },
        relax: {
          colors: ['#f3f3f3', '#d7d8d5', '#ded8cd', '#d2cbb1', '#e7d6c0', '#eddb8d', '#b4531e', '#bfd297', '#5d5f25', '#c6afcd', '#d0aead', '#e03144', '#8a1f1e', '#6b263e', '#9ba9ba', '#6a7390', '#707b7e', '#5e5f58', '#444440', '#34443b', '#171618'],
          plusMore: 27
        },
        DayAndNight: {
          colors: ['#F7F7F7', '#ECE7E1', '#EFE9C9', '#F2CE92', '#E17439', '#C73830', '#DDD16F', '#858AA0', '#50BAA4', '#323953', '#53423A', '#BEBEBE', '#716766', '#2B2B2B'],
          plusMore: 0
        },
        Thermo: {
          colors: ['#F2F2F2', '#B4B5B2', '#E1D5A0', '#BAA386', '#E3C3C3', '#8A8C54', '#8D2A2E', '#35516A', '#C77531', '#625243', '#5F5F5F', '#292929'],
          plusMore: 0
        },
        Millenium: {
          colors: ['#e1e0dd', '#a08f75', '#a5a292', '#4e4739', '#5e6525', '#9c7a2c', '#cd642c', '#6c2b2b', '#b12d2c', '#bdb6b6', '#9ca9ad', '#101f2b', '#405854', '#2a3d30', '#8d928a', '#524944', '#2f2c2b', '#131312', '#452636'],
          plusMore: 0
        },
        Century: {
          colors: ['#f1f0ee', '#b3b1b0', '#d2c3b0', '#d0a966', '#cb5838', '#933245', '#7f8481', '#5b5c59', '#4c3d33', '#232322', '#f0efed', '#b9b7b5', '#d5c5b1', '#d0ae6b', '#cb5e3b', '#97364c', '#8a8f8c', '#4b4c49', '#55453a', '#232322'],
          plusMore: 0
        },
        Spannbettlaken: {
          colors: ['#f4f3f1', '#eedcc6', '#f7e59f', '#deded9', '#8b817e', '#f1d0da', '#ea2a41', '#c9da9b', '#3a6e5e', '#d3bfdc', '#79223f', '#875f4e', '#6c7394', '#787e99', '#a3b0c5', '#b9d0c2', '#cbceac', '#dabebd', '#d9d1ba'],
          plusMore: 0
        },
        Kissenbezüge: {
          colors: ['#e4ded2', '#8c7757', '#a29c8b', '#4e3d31', '#5e6323', '#8e6e29', '#c56024',
            '#6b2a27', '#b12a27', '#ae8c9d', '#6e7f86', '#0d1e2d', '#2b3336', '#374c40',
            '#1f3727', '#686a63', '#46413c', '#444340', '#131312', '#4a2e3c'],
          plusMore: 0
        }
      }
    }
  },
  mounted () {
    this.resize()
    window.addEventListener('resize', this.resize)
  },
  destroyed () {
    window.removeEventListener('resize', this.resize)
  },
  methods: {
    resize () {
      this.setEqualHeight(this.$refs.categoryTiles, '.category-links', true)
      this.setEqualHeight(this.$refs.categoryTiles, '.category-header-container', true)
      this.setEqualHeight(this.$refs.categoryTiles, '.rating-avarage', true)
    },
    getColors (colors) {
      if (colors) {
        return colors.filter(x => x.hex)
      }
      return []
    },
    getColorsByCategory (category) {
      return this.colors[category?.description?.name_seo] ? this.colors[category.description.name_seo] : this.colors[category?.identifier] ? this.colors[category.identifier] : []
    },
    getLink (category) {
      if (this.home) {
        // return this.getLinkProduct(category)
        return this.getLinkCategories(category.identifier)
      } else {
        return this.getLinkProduct(category)
      }
    },
    getLinkCategories (identifier) {
      switch (identifier) {
        case 'plisy':
          return this.localePath('categories-pleated-blinds')
        case 'roletki':
          return this.localePath('categories-roller-blinds')
        case 'zaslony':
          return this.localePath('categories-curtains')
        case 'relax':
          return this.localePath('categories-relax')
        default:
          return null
      }
    },

    getLinkProduct (category) {
      if (category && category.offerProductName && category.offerProduct) {
        return this.localePath({
          name: 'product',
          params: {
            productName: category.link,
            productId: category.offerProduct
          }
        })
      }
      return null
    },
    checkType (category, type) {
      if (this.home) {
        return category.identifier === type
      } else {
        return category &&
        category.description &&
        category.description.name_seo &&
        category.description.name_seo === type
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.category-tiles {
  @include fullhd {
    margin: 0 -172px !important;
  }
  @include touch {
    margin: 0 -25px !important;
  }
  :deep(.carousel-item) {
    cursor: default;
  }
  :deep(.carousel-content) {
    @include touch {
      padding: 24px 0;
    }
  }
  .category-description{
    font-weight: 600;
  }
}
</style>
